import * as DialogPrimitive from "@radix-ui/react-dialog";
import { SearchX, X } from "lucide-react";
import CategoryProgress from "./CategoryProgress";
import MetricsList from "./MetricsList";
import MetricsSearch from "./MetricsSearch";
import MetricsSummaryCard from "./MetricsSummaryCard";

const DRAWER_VARIANTS = {
  data: {
    title: "Data Entry Completion",
    description:
      "Review which assigned metrics already have submitted data and which are still pending.",
    searchPlaceholder: "Search by metric code or metric title",
    emptyScopeTitle: "No assigned metrics found",
    emptyScopeDescription:
      "There are no assigned metrics available in the current dashboard scope.",
    emptySearchTitle: "No metrics match this search",
    emptySearchDescription:
      "Try a different metric code or title. Summary totals and category progress remain unchanged.",
    completedTitle: "Completed Metrics",
    completedHint: "Metrics with data submitted",
    completedEmptyMessage: "No completed metrics match the current view.",
    missingTitle: "Missing Metrics",
    missingHint: "Metrics still waiting for data",
    missingEmptyMessage: "No missing metrics match the current view.",
    countingHint:
      "Completed means the metric has a submitted answer for the selected scope and year, including answers marked n.a; blank means not yet submitted.",
  },
  target: {
    title: "Target Setup Completion",
    description:
      "Review which assigned metrics already have targets defined and which are still pending.",
    searchPlaceholder: "Search by metric code or metric title",
    emptyScopeTitle: "No assigned metrics found",
    emptyScopeDescription:
      "There are no assigned metrics available in the current dashboard scope.",
    emptySearchTitle: "No metrics match this search",
    emptySearchDescription:
      "Try a different metric code or title. Summary totals and category progress remain unchanged.",
    completedTitle: "Completed Metrics",
    completedHint: "Metrics with targets defined",
    completedEmptyMessage: "No completed metrics match the current view.",
    missingTitle: "Missing Metrics",
    missingHint: "Metrics still waiting for target setup",
    missingEmptyMessage: "No missing metrics match the current view.",
  },
};

const LoadingState = ({ completedTitle, missingTitle }) => (
  <div className="space-y-3">
    <div className="space-y-3">
      <MetricsList
        title={completedTitle}
        metrics={[]}
        totalCount={0}
        status="completed"
        emptyMessage=""
        loading
      />
      <MetricsList
        title={missingTitle}
        metrics={[]}
        totalCount={0}
        status="missing"
        emptyMessage=""
        loading
      />
    </div>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="rounded-lg border border-dashed border-border/70 bg-muted px-4 py-8 text-center">
    <div className="mx-auto max-w-md">
      <span className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-md bg-card text-muted-foreground shadow-sm">
        <SearchX className="h-4 w-4" />
      </span>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
        {description}
      </p>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry, t }) => (
  <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center rounded-full border border-destructive/30 bg-background px-4 py-2 text-sm font-medium text-destructive transition hover:border-destructive/40 hover:text-destructive"
      >
        {t("Try again")}
      </button>
    </div>
  </div>
);

const MetricsDrawer = ({
  assignedMetrics,
  completedMetrics,
  missingMetrics,
  filteredCompletedMetrics,
  filteredMissingMetrics,
  categorySummary,
  loading,
  error,
  search,
  onSearchChange,
  onRetry,
  variant = "data",
  frameworkLabelByCode,
  t,
}) => {
  const totalCount = assignedMetrics.length;
  const filteredTotalCount =
    filteredCompletedMetrics.length + filteredMissingMetrics.length;
  const hasSearchQuery = search.trim().length > 0;
  const showScopeEmptyState = !loading && totalCount === 0;
  const showSearchEmptyState = hasSearchQuery && filteredTotalCount === 0;
  const showListLoadingState = loading;
  const copy = DRAWER_VARIANTS[variant] || DRAWER_VARIANTS.data;
  const moduleName = "data-entry";

  return (
    <>
      <div className="sticky top-0 z-20 border-b border-border/70 bg-background px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-base font-semibold tracking-tight text-foreground">
              {t(copy.title)}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-1 text-xs leading-5 text-muted-foreground">
              {t(copy.description)}
            </DialogPrimitive.Description>
          </div>

          <DialogPrimitive.Close className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground shadow-sm transition hover:border-border hover:text-foreground">
            <X className="h-5 w-5" />
            <span className="sr-only">{t("Close")}</span>
          </DialogPrimitive.Close>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="flex min-h-full flex-col gap-3 xl:h-full xl:min-h-0">
          {error ? (
            <ErrorState message={error} onRetry={onRetry} t={t} />
          ) : null}

          <MetricsSummaryCard
            completedCount={completedMetrics.length}
            missingCount={missingMetrics.length}
            totalCount={totalCount}
            variant={variant}
            loading={loading}
          />

          <CategoryProgress items={categorySummary} loading={loading} />
          {copy.countingHint ? (
            <p className="text-xs leading-5 text-muted-foreground">
              {t(copy.countingHint)}
            </p>
          ) : null}
          <div className="h-px bg-border/70" />

          <MetricsSearch
            value={search}
            onChange={onSearchChange}
            placeholder={t(copy.searchPlaceholder)}
          />

          {showListLoadingState ? (
            <LoadingState
              completedTitle={t(copy.completedTitle)}
              missingTitle={t(copy.missingTitle)}
            />
          ) : showScopeEmptyState ? (
            <EmptyState
              title={t(copy.emptyScopeTitle)}
              description={t(copy.emptyScopeDescription)}
            />
          ) : showSearchEmptyState ? (
            <EmptyState
              title={t(copy.emptySearchTitle)}
              description={t(copy.emptySearchDescription)}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <MetricsList
                title={t(copy.completedTitle)}
                moduleName={moduleName}
                metrics={filteredCompletedMetrics}
                totalCount={completedMetrics.length}
                status="completed"
                variant={variant}
                emptyHint={t(copy.completedHint)}
                emptyMessage={t(copy.completedEmptyMessage)}
                frameworkLabelByCode={frameworkLabelByCode}
              />

              <MetricsList
                title={t(copy.missingTitle)}
                moduleName={moduleName}
                metrics={filteredMissingMetrics}
                totalCount={missingMetrics.length}
                status="missing"
                variant={variant}
                emptyHint={t(copy.missingHint)}
                emptyMessage={t(copy.missingEmptyMessage)}
                frameworkLabelByCode={frameworkLabelByCode}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MetricsDrawer;
